import { useState } from "react"
import { useQuery } from "@rocicorp/zero/react"

import { Select } from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import {
	CHANNEL_PICKER_LIMIT,
	channelPickerOptions,
	DISABLE_CHANNEL_VALUE,
	savedChannelLabel,
	shouldRefreshChannels,
} from "./slack-channel-list"

// Shared by every picker on the page, so opening both pickers asks Slack once.
const lastRefreshAt = new Map<string, number>()

function useRefreshSlackChannels(slackConnectSettingsId: string) {
	const utils = api.useUtils()
	const sync = api.workspace.syncSlackChannels.useMutation({
		onSuccess: () => void utils.workspace.slackSettings.invalidate(),
		onError: (error) =>
			toast.error({
				title: "Channels didn't refresh",
				description: error.message,
			}),
	})
	return () => {
		const now = Date.now()
		if (!shouldRefreshChannels(lastRefreshAt.get(slackConnectSettingsId), now))
			return
		lastRefreshAt.set(slackConnectSettingsId, now)
		sync.mutate({ slackConnectSettingsId })
	}
}

/**
 * Picks the channel a kind of notification goes to. Searches every channel the
 * bot is in or can join, and joins a public channel when it is picked.
 */
export function SlackChannelSelect({
	name,
	slackConnectSettingsId,
	value,
	excludeChannelId,
	onChange,
}: {
	name: string
	slackConnectSettingsId: string
	value: string | null
	/** Left out of the list, so one channel never gets both kinds of notification. */
	excludeChannelId?: string | null
	onChange: (channelId: string | null) => Promise<void>
}) {
	const utils = api.useUtils()
	const [open, setOpen] = useState(false)
	const [search, setSearch] = useState("")
	const [joiningName, setJoiningName] = useState<string | null>(null)

	const [channels] = useQuery(
		queries.slackChannelPicker({
			slackConnectSettingsId,
			search: search.slice(0, 100),
			limit: CHANNEL_PICKER_LIMIT,
		}),
		{ enabled: open },
	)
	const [savedRows, savedResult] = useQuery(
		queries.slackChannelsByChannelIds({ channelIds: value ? [value] : [] }),
		{ enabled: !!value },
	)
	const saved = savedRows.find(
		(row) => row.slackConnectSettingsId === slackConnectSettingsId,
	)

	const refresh = useRefreshSlackChannels(slackConnectSettingsId)
	const join = api.workspace.joinSlackChannels.useMutation()

	const pick = async (next: string) => {
		if (next === DISABLE_CHANNEL_VALUE) return onChange(null)
		const channel = channels.find((row) => row.channelId === next)
		if (channel && !channel.isMember) {
			setJoiningName(channel.name)
			try {
				// The inbox stays off: a notification channel isn't where customers write in.
				const result = await join.mutateAsync({
					slackConnectSettingsId,
					slackChannelIds: [channel.id],
					inbox: false,
				})
				if (result.failed > 0) {
					toast.error({ title: "Slack didn't let the bot join that channel" })
					return
				}
				void utils.workspace.slackSettings.invalidate()
			} catch (error) {
				toast.error({
					title: "The bot didn't join",
					description: error instanceof Error ? error.message : undefined,
				})
				return
			} finally {
				setJoiningName(null)
			}
		}
		await onChange(next)
	}

	return (
		<Select
			searchable
			name={name}
			value={value ?? DISABLE_CHANNEL_VALUE}
			displayValue={
				joiningName ??
				savedChannelLabel(value, saved, savedResult.type === "complete")
			}
			options={channelPickerOptions(channels, { search, excludeChannelId })}
			onSearchChange={setSearch}
			onOpenChange={(next) => {
				setOpen(next)
				if (next) refresh()
			}}
			onChange={(next) => void pick(next)}
			className="w-48"
			selectClassName="dark:bg-gray-700/70"
			popoverClassName="w-64"
			popoverAlign="end"
		/>
	)
}
